import type { LucideIcon } from 'lucide-react';
import { CheckCircle2, Heart, MessageCircle, Sparkles } from 'lucide-react';
import { Fragment, type ReactNode, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from '../../../../utils';
import { Popover, PopoverContent, PopoverTrigger } from '../../../core/popover';
import { Tooltip, TooltipContent, TooltipTrigger } from '../../../core/tooltip';
import type {
  ExerciseFeedbackMessage,
  ExerciseFeedbackSource,
  ExerciseFeedbackVariant,
} from '../../hooks/content-renderer-types';

function useHasHover(): boolean {
  // Default to true so SSR/first paint matches the desktop case; touch devices
  // flip to false after mount.
  const [hasHover, setHasHover] = useState(true);

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return;
    const query = window.matchMedia('(hover: hover)');
    setHasHover(query.matches);
    const onChange = (event: MediaQueryListEvent) => setHasHover(event.matches);
    query.addEventListener('change', onChange);
    return () => query.removeEventListener('change', onChange);
  }, []);

  return hasHover;
}

interface AnnotatedSegment {
  type: 'text' | 'annotation';
  content: string;
  label?: string;
  tooltip?: string;
}

/**
 * Annotation convention (one consistent meaning per colour):
 *
 * - `correction` (rose) — something to fix. Labels `spelling`, `grammar`,
 *   `vocabulary`, `comprehension`, `error`, and any label we don't know.
 * - `tip` (sky) — a note on a word the student got RIGHT: praise or a possible
 *   refinement. Only the `style` label (see the evaluation prompt in
 *   `packages/agents/src/agents/evaluation/prompts.ts`).
 *
 * The original label is kept on the span as `data-annotation-label`; the
 * family is on `data-annotation-family`. A legend explaining the two colours
 * is rendered once above every annotated text that has at least one annotation.
 */
export type AnnotationFamily = 'correction' | 'tip';

const TIP_LABELS = new Set(['style']);

export function annotationFamily(label: string | undefined): AnnotationFamily {
  return label && TIP_LABELS.has(label.toLowerCase()) ? 'tip' : 'correction';
}

const ANNOTATION_FAMILY_STYLE: Record<AnnotationFamily, string> = {
  correction:
    'bg-rose-100/80 text-rose-800 border-b-2 border-rose-300 focus-visible:ring-rose-300',
  tip: 'bg-sky-100/80 text-sky-800 border-b-2 border-sky-300 focus-visible:ring-sky-300',
};

const LEGEND_SWATCH_STYLE: Record<AnnotationFamily, string> = {
  correction: 'bg-rose-100 border-rose-300',
  tip: 'bg-sky-100 border-sky-300',
};

const LEGEND_TITLE_STYLE: Record<AnnotationFamily, string> = {
  correction: 'text-rose-800',
  tip: 'text-sky-800',
};

function parseAnnotatedText(rawText: string): AnnotatedSegment[] {
  const segments: AnnotatedSegment[] = [];

  // Match either:
  //   Form A: {{word}}[[label#tooltip]]   — canonical replacement form
  //   Form B: word{{label#tooltip}}       — inline form appended to a word
  const pattern =
    /\{\{([^{}]*?)\}\}\[\[(?:(\w+?)#)?([^\]]*?)\]\]|([^\s{}]+)\{\{(\w+)#([^{}]*?)\}\}/g;

  let lastIndex = 0;
  let match: RegExpExecArray | null;

  while ((match = pattern.exec(rawText)) !== null) {
    const matchIndex = match.index;

    if (match[1] !== undefined) {
      if (matchIndex > lastIndex) {
        segments.push({ type: 'text', content: rawText.slice(lastIndex, matchIndex) });
      }
      segments.push({
        type: 'annotation',
        content: match[1],
        label: (match[2] ?? 'error').toLowerCase(),
        tooltip: match[3] ?? '',
      });
    } else if (match[4] !== undefined) {
      if (matchIndex > lastIndex) {
        segments.push({ type: 'text', content: rawText.slice(lastIndex, matchIndex) });
      }
      segments.push({
        type: 'annotation',
        content: match[4],
        label: (match[5] ?? 'error').toLowerCase(),
        tooltip: match[6] ?? '',
      });
    }

    lastIndex = matchIndex + match[0].length;
  }

  if (lastIndex < rawText.length) {
    segments.push({ type: 'text', content: rawText.slice(lastIndex) });
  }

  const cleaned = segments
    .map((segment) => {
      if (segment.type !== 'text') return segment;
      const content = segment.content
        .replace(/\{\{\w+#[^{}]*?\}\}/g, '')
        .replace(/\{\{([^{}]*?)\}\}/g, '$1')
        .replace(/\[\[([^\]]*?)$/g, '');
      return { ...segment, content };
    })
    .filter((segment) => segment.type !== 'text' || segment.content.length > 0);

  if (!cleaned.length) {
    return [{ type: 'text', content: rawText }];
  }

  return cleaned;
}

function renderInlineMarkdown(text: string, keyPrefix: string): ReactNode[] {
  const tokens: ReactNode[] = [];
  const pattern = /\*\*([^*]+)\*\*|\*([^*\s][^*]*?)\*/g;
  let lastIndex = 0;
  let match: RegExpExecArray | null;
  let counter = 0;

  while ((match = pattern.exec(text)) !== null) {
    if (match.index > lastIndex) {
      tokens.push(text.slice(lastIndex, match.index));
    }
    if (match[1] !== undefined) {
      tokens.push(<strong key={`${keyPrefix}-b-${counter++}`}>{match[1]}</strong>);
    } else if (match[2] !== undefined) {
      tokens.push(<em key={`${keyPrefix}-i-${counter++}`}>{match[2]}</em>);
    }
    lastIndex = match.index + match[0].length;
  }
  if (lastIndex < text.length) {
    tokens.push(text.slice(lastIndex));
  }
  return tokens.length > 0 ? tokens : [text];
}

function renderFeedbackMarkdown(content: string): ReactNode {
  const paragraphs = content.split(/\n{2,}/);

  return (
    <>
      {paragraphs.map((paragraph, pIdx) => {
        const lines = paragraph.split('\n').filter((line) => line.length > 0);
        if (lines.length === 0) return null;

        const isList =
          lines.length > 1 && lines.every((line) => /^\s*\d+\.\s+/.test(line));

        if (isList) {
          return (
            <ol
              key={`p-${pIdx}`}
              className={cn('list-decimal space-y-1 pl-5', pIdx > 0 && 'mt-2')}
            >
              {lines.map((line, lIdx) => {
                const text = line.replace(/^\s*\d+\.\s+/, '');
                return (
                  <li key={`p-${pIdx}-li-${lIdx}`}>
                    {renderInlineMarkdown(text, `p-${pIdx}-li-${lIdx}`)}
                  </li>
                );
              })}
            </ol>
          );
        }

        return (
          <p key={`p-${pIdx}`} className={pIdx > 0 ? 'mt-2' : undefined}>
            {lines.map((line, lIdx) => (
              <Fragment key={`p-${pIdx}-l-${lIdx}`}>
                {renderInlineMarkdown(line, `p-${pIdx}-l-${lIdx}`)}
                {lIdx < lines.length - 1 && <br />}
              </Fragment>
            ))}
          </p>
        );
      })}
    </>
  );
}

const ANNOTATION_BASE_CLASS =
  'mx-0.5 inline-flex cursor-help items-baseline rounded-md px-1.5 py-0.5 align-baseline text-sm font-medium outline-none transition-colors focus-visible:ring-2';

function AnnotationWithTooltip({
  content,
  tooltip,
  label,
  family,
  hasHover,
}: {
  content: string;
  tooltip: string;
  label: string;
  family: AnnotationFamily;
  hasHover: boolean;
}) {
  const trigger = (
    <span
      tabIndex={0}
      data-annotation-label={label}
      data-annotation-family={family}
      className={cn(ANNOTATION_BASE_CLASS, ANNOTATION_FAMILY_STYLE[family])}
    >
      {content}
    </span>
  );

  if (hasHover) {
    return (
      <Tooltip>
        <TooltipTrigger asChild>{trigger}</TooltipTrigger>
        <TooltipContent
          side="top"
          sideOffset={6}
          collisionPadding={8}
          className="max-w-[240px] whitespace-normal rounded-xl bg-slate-900 px-3 py-2 text-xs font-normal text-white shadow-xl"
        >
          {tooltip}
        </TooltipContent>
      </Tooltip>
    );
  }

  return (
    <Popover>
      <PopoverTrigger asChild>{trigger}</PopoverTrigger>
      <PopoverContent
        side="top"
        sideOffset={6}
        collisionPadding={8}
        className="w-auto max-w-[240px] whitespace-normal rounded-xl border-0 bg-slate-900 px-3 py-2 text-xs font-normal text-white shadow-xl"
      >
        {tooltip}
      </PopoverContent>
    </Popover>
  );
}

const LEGEND_FAMILIES: AnnotationFamily[] = ['correction', 'tip'];

/** Compact two-swatch key explaining what the rose and sky boxes mean. */
function AnnotationLegend() {
  const { t } = useTranslation('p2e');

  return (
    <div
      data-testid="annotation-legend"
      className="mb-3 flex flex-wrap items-center gap-x-4 gap-y-1 border-b border-dashed border-slate-200 pb-2.5 text-[11px] leading-snug text-slate-500"
    >
      {LEGEND_FAMILIES.map((family) => (
        <span key={family} className="inline-flex items-center gap-1.5">
          <span
            aria-hidden="true"
            className={cn(
              'inline-block h-3 w-3 shrink-0 rounded-sm border-b-2',
              LEGEND_SWATCH_STYLE[family],
            )}
          />
          <span>
            <span className={cn('font-semibold', LEGEND_TITLE_STYLE[family])}>
              {t(`exerciseFeedback.legend.${family}.title`)}
            </span>
            {' — '}
            {t(`exerciseFeedback.legend.${family}.hint`)}
          </span>
        </span>
      ))}
    </div>
  );
}

function AnnotatedText({ text }: { text: string }) {
  const segments = parseAnnotatedText(text);
  const hasHover = useHasHover();
  const hasAnnotations = segments.some((segment) => segment.type === 'annotation');

  return (
    <div className="mt-4 rounded-2xl bg-white/60 px-5 py-4 text-[15px] leading-relaxed text-slate-700 ring-1 ring-slate-200/50">
      {hasAnnotations && <AnnotationLegend />}
      <div className="whitespace-pre-wrap">
        {segments.map((segment, index) => {
          if (segment.type === 'annotation') {
            const label = segment.label ?? 'error';
            const family = annotationFamily(label);

            if (!segment.tooltip) {
              return (
                <span
                  key={`annotation-${index}`}
                  tabIndex={0}
                  data-annotation-label={label}
                  data-annotation-family={family}
                  className={cn(ANNOTATION_BASE_CLASS, ANNOTATION_FAMILY_STYLE[family])}
                >
                  {segment.content}
                </span>
              );
            }

            return (
              <AnnotationWithTooltip
                key={`annotation-${index}`}
                content={segment.content}
                tooltip={segment.tooltip}
                label={label}
                family={family}
                hasHover={hasHover}
              />
            );
          }

          return <span key={`text-${index}`}>{segment.content}</span>;
        })}
      </div>
    </div>
  );
}

type VariantKey = NonNullable<ExerciseFeedbackVariant> | 'default';

interface VariantTheme {
  wrapper: string;
  accent: string;
  icon: LucideIcon;
  iconWrapper: string;
  title: string;
  description: string;
}

const VARIANT_THEMES: Record<VariantKey, VariantTheme> = {
  success: {
    wrapper:
      'bg-gradient-to-br from-[#cf2e2e] via-[#f05f7a] to-[#f78da7] text-white shadow-[0_20px_50px_rgba(207,46,46,0.25)]',
    accent: 'bg-white/20',
    icon: Heart,
    iconWrapper: 'bg-white/25 text-white',
    title: 'text-white',
    description: 'text-white/90',
  },
  warning: {
    wrapper:
      'bg-gradient-to-br from-amber-50 via-orange-50 to-yellow-50 border border-amber-200/60 shadow-[0_15px_40px_rgba(245,158,11,0.12)]',
    accent: 'bg-amber-500/10',
    icon: Sparkles,
    iconWrapper: 'bg-amber-100 text-amber-600',
    title: 'text-amber-900',
    description: 'text-amber-800/80',
  },
  info: {
    wrapper:
      'bg-gradient-to-br from-sky-50 via-cyan-50 to-teal-50 border border-sky-200/60 shadow-[0_15px_40px_rgba(14,165,233,0.12)]',
    accent: 'bg-sky-500/10',
    icon: Sparkles,
    iconWrapper: 'bg-sky-100 text-sky-600',
    title: 'text-sky-900',
    description: 'text-sky-800/80',
  },
  teacher: {
    wrapper:
      'bg-gradient-to-br from-violet-50 via-purple-50 to-fuchsia-50 border border-violet-200/60 shadow-[0_15px_40px_rgba(139,92,246,0.15)]',
    accent: 'bg-violet-500/10',
    icon: MessageCircle,
    iconWrapper: 'bg-violet-100 text-violet-600',
    title: 'text-violet-900',
    description: 'text-violet-800/80',
  },
  destructive: {
    wrapper:
      'bg-gradient-to-br from-rose-50 via-red-50 to-orange-50 border border-rose-200/60 shadow-[0_15px_40px_rgba(244,63,94,0.15)]',
    accent: 'bg-rose-500/10',
    icon: Sparkles,
    iconWrapper: 'bg-rose-100 text-rose-600',
    title: 'text-rose-900',
    description: 'text-rose-800/80',
  },
  default: {
    wrapper: 'bg-white border border-slate-200/80 shadow-[0_10px_30px_rgba(15,23,42,0.06)]',
    accent: 'bg-slate-100',
    icon: CheckCircle2,
    iconWrapper: 'bg-slate-100 text-slate-600',
    title: 'text-slate-900',
    description: 'text-slate-600',
  },
};

const SOURCE_LABELS: Record<ExerciseFeedbackSource, string> = {
  auto: 'Result',
  ai: 'AI Feedback',
  status: 'Status',
  teacher: 'From your teacher',
};

interface ExerciseFeedbackListProps {
  feedback: ExerciseFeedbackMessage[];
  /**
   * The answer is being processed by the backend: a quiet "Awaiting
   * correction…" card replaces the messages until the new result arrives,
   * so a stale correction is never shown while a resubmission is graded.
   */
  pending?: boolean;
}

/** After this long the hint says the correction is taking longer than usual. */
export const PENDING_SLOW_AFTER_MS = 5000;

/** The legacy (non-zine) card shown while a submission is in flight. */
function PendingFeedbackCard() {
  const { t } = useTranslation('p2e');
  const theme = VARIANT_THEMES.default;
  const [slow, setSlow] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setSlow(true), PENDING_SLOW_AFTER_MS);
    return () => clearTimeout(timer);
  }, []);

  return (
    <div
      role="status"
      data-testid="feedback-pending"
      className={cn('relative rounded-3xl px-6 py-6', theme.wrapper)}
    >
      <div className="flex items-start gap-4">
        <div
          className={cn(
            'flex h-11 w-11 shrink-0 items-center justify-center rounded-xl',
            theme.iconWrapper,
          )}
        >
          <span
            aria-hidden="true"
            className="h-5 w-5 animate-spin rounded-full border-2 border-current border-t-transparent"
          />
        </div>

        <div className="min-w-0 flex-1">
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">
            {SOURCE_LABELS.status}
          </p>
          <p className={cn('mt-1 text-lg font-semibold leading-tight', theme.title)}>
            {t('exerciseFeedback.pending.title')}
          </p>
          <p className={cn('mt-1 text-[15px] leading-relaxed', theme.description)}>
            {t(slow ? 'exerciseFeedback.pending.slowHint' : 'exerciseFeedback.pending.hint')}
          </p>
        </div>
      </div>
    </div>
  );
}

export function ExerciseFeedbackList({ feedback, pending = false }: ExerciseFeedbackListProps) {
  if (pending) {
    return <PendingFeedbackCard />;
  }

  if (!feedback?.length) {
    return null;
  }

  return (
    <div className="space-y-4">
      {feedback.map((item, index) => {
        const variantKey: VariantKey = item.variant ?? 'default';
        const theme = VARIANT_THEMES[variantKey];
        const Icon = theme.icon;
        const sourceLabel = SOURCE_LABELS[item.source] ?? SOURCE_LABELS.status;
        const hasBody = Boolean(item.description) || Boolean(item.annotatedText);

        // Success variant - celebratory full-width banner
        if (variantKey === 'success') {
          return (
            <div
              key={`${item.source}-${index}`}
              className={cn(
                'relative overflow-hidden rounded-3xl px-6 py-7 transition-all duration-300',
                'hover:shadow-[0_25px_60px_rgba(207,46,46,0.3)] hover:scale-[1.01]',
                theme.wrapper,
              )}
            >
              {/* Decorative circles */}
              <div className="pointer-events-none absolute -right-8 -top-8 h-32 w-32 rounded-full bg-white/10" />
              <div className="pointer-events-none absolute -bottom-4 -left-4 h-20 w-20 rounded-full bg-white/10" />

              <div className="relative flex items-center gap-5">
                <div
                  className={cn(
                    'flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl',
                    theme.iconWrapper,
                  )}
                >
                  <Icon className="h-7 w-7" strokeWidth={2} />
                </div>

                <div className="min-w-0 flex-1">
                  <p className="text-xs font-semibold uppercase tracking-[0.25em] text-white/70">
                    {sourceLabel}
                  </p>
                  <p className="mt-1 text-2xl font-bold leading-tight">{item.title}</p>
                  {item.description && (
                    <div className="mt-2 text-base leading-relaxed text-white/85">
                      {renderFeedbackMarkdown(item.description)}
                    </div>
                  )}
                </div>
              </div>

              {item.annotatedText && <AnnotatedText text={item.annotatedText} />}
            </div>
          );
        }

        // Teacher variant - personal note style
        if (variantKey === 'teacher') {
          return (
            <div
              key={`${item.source}-${index}`}
              data-feedback-source="teacher"
              className={cn(
                'relative rounded-3xl px-6 py-6 transition-all duration-300',
                'hover:shadow-[0_20px_50px_rgba(139,92,246,0.2)]',
                theme.wrapper,
              )}
            >
              <div className="flex items-start gap-4">
                <div
                  className={cn(
                    'flex h-12 w-12 shrink-0 items-center justify-center rounded-full',
                    theme.iconWrapper,
                  )}
                >
                  <Icon className="h-5 w-5" strokeWidth={2} />
                </div>

                <div className="min-w-0 flex-1 pt-1">
                  <p className={cn('text-sm font-semibold', theme.title)}>{sourceLabel}</p>
                  {hasBody && (
                    <div className="mt-3">
                      {item.description && (
                        <p
                          className={cn(
                            'text-[15px] leading-relaxed',
                            theme.description,
                          )}
                          style={{ fontStyle: 'italic' }}
                        >
                          "{item.description}"
                        </p>
                      )}
                      {item.annotatedText && <AnnotatedText text={item.annotatedText} />}
                    </div>
                  )}
                </div>
              </div>
            </div>
          );
        }

        // Warning/Info/Default - clean card style
        return (
          <div
            key={`${item.source}-${index}`}
            className={cn(
              'relative rounded-3xl px-6 py-6 transition-all duration-200',
              'hover:shadow-lg',
              theme.wrapper,
            )}
          >
            <div className="flex items-start gap-4">
              <div
                className={cn(
                  'flex h-11 w-11 shrink-0 items-center justify-center rounded-xl',
                  theme.iconWrapper,
                )}
              >
                <Icon className="h-5 w-5" strokeWidth={2} />
              </div>

              <div className="min-w-0 flex-1">
                <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">
                  {sourceLabel}
                </p>
                <p className={cn('mt-1 text-lg font-semibold leading-tight', theme.title)}>
                  {item.title}
                </p>
              </div>
            </div>

            {hasBody && (
              <div className="mt-3">
                {item.description && (
                  <div className={cn('text-[15px] leading-relaxed', theme.description)}>
                    {renderFeedbackMarkdown(item.description)}
                  </div>
                )}
                {item.annotatedText && <AnnotatedText text={item.annotatedText} />}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
